"use client";

import {
	FloatingArrow,
	FloatingOverlay,
	arrow,
	autoPlacement,
	autoUpdate,
	offset,
	shift,
	useFloating,
} from "@floating-ui/react";
import { Slot } from "@radix-ui/react-slot";
import { useRouter } from "next/navigation";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Button } from "./button";
import { Card, CardContent, CardFooter } from "./card";
import { Icon } from "./icon";
import { cn } from "./lib/utils";

const waitForElement = (
	selector: string,
	timeout = 10000,
): Promise<HTMLElement> => {
	return new Promise((resolve, reject) => {
		const element = document.querySelector(selector);
		if (element instanceof HTMLElement) {
			resolve(element);
			return;
		}

		const startTime = Date.now();
		const observer = new MutationObserver(() => {
			const element = document.querySelector(selector);
			if (element instanceof HTMLElement) {
				observer.disconnect();
				resolve(element);
			} else if (Date.now() - startTime > timeout) {
				observer.disconnect();
				reject(new Error(`Element ${selector} not found after ${timeout}ms`));
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		// Also check periodically in case the element exists but no mutations occur
		const interval = setInterval(() => {
			const element = document.querySelector(selector);
			if (element instanceof HTMLElement) {
				observer.disconnect();
				clearInterval(interval);
				resolve(element);
			} else if (Date.now() - startTime > timeout) {
				observer.disconnect();
				clearInterval(interval);
				reject(new Error(`Element ${selector} not found after ${timeout}ms`));
			}
		}, 100);
	});
};

export interface TourContext {
	start: () => void;
	end: () => void;
	nextStep: () => void;
	prevStep: () => void;
	currentStepIndex: number;
	currentStep: TourStep;
	steps: TourStep[];
	isLastStep: boolean;
	isEnabled: boolean;
	disableManualExit: boolean;
}

export interface TourStep {
	target: string;
	step: React.ReactNode;
	onNextClick?: (
		targetElement: Element,
		router: ReturnType<typeof useRouter>,
	) => void;
	onShow?: (targetElement: Element, tour: TourContext) => void | Promise<void>;
	hideNextButton?: boolean;
}

interface UseTourProps {
	steps: TourStep[];
	onFinish?: () => void;
	isOpen?: boolean;
	onClose?: () => void;
	noPreviousButton?: boolean;
	disableManualExit?: boolean;
}

export function useTour(props?: UseTourProps) {
	const router = useRouter();
	const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(
		props?.isOpen ? 0 : null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const arrowRef = useRef(null);
	const lastExecutedStepRef = useRef<number | null>(null);

	const { elements, refs, floatingStyles, update, context } = useFloating({
		middleware: [
			offset(10),
			shift(),
			autoPlacement(),
			arrow({ element: arrowRef }),
		],
		whileElementsMounted: autoUpdate,
	});

	useEffect(() => {
		if (props?.isOpen) {
			setCurrentStepIndex(0);
			lastExecutedStepRef.current = null;
		} else {
			setCurrentStepIndex(null);
			lastExecutedStepRef.current = null;
		}
	}, [props?.isOpen]);

	const start = useCallback(() => {
		setCurrentStepIndex(0);
	}, []);

	const end = useCallback(() => {
		setCurrentStepIndex(null);
		props?.onClose?.();
	}, [props?.onClose]);

	const nextStep = useCallback(() => {
		if (currentStepIndex !== null) {
			setIsLoading(true);
			const currentStep = props?.steps?.[currentStepIndex];
			const referenceElement = elements.reference;
			if (currentStep?.onNextClick && referenceElement) {
				currentStep.onNextClick(referenceElement as Element, router);
			}

			if (currentStepIndex < (props?.steps?.length ?? 0) - 1) {
				const nextStepIndex = currentStepIndex + 1;
				const nextStepSelector = props?.steps?.[nextStepIndex]?.target;
				const nextStep = props?.steps?.[nextStepIndex];

				if (!nextStepSelector) {
					console.warn("Next step target selector is missing");
					end();
					setIsLoading(false);
					return;
				}

				// Create a promise that resolves after 5 seconds
				const timeoutPromise = new Promise((_, reject) => {
					setTimeout(() => {
						reject(new Error("Timeout waiting for element"));
					}, 5000);
				});

				// Race between finding the element and timeout
				Promise.race([waitForElement(nextStepSelector), timeoutPromise])
					.then((target) => {
						// Check if the element is in viewport
						const rect = (target as HTMLElement).getBoundingClientRect();
						const isVisible =
							rect.top >= 0 &&
							rect.left >= 0 &&
							rect.bottom <=
								(window.innerHeight || document.documentElement.clientHeight) &&
							rect.right <=
								(window.innerWidth || document.documentElement.clientWidth);

						if (isVisible) {
							setCurrentStepIndex(nextStepIndex);
						} else {
							// Scroll element into view and then set the step
							(target as HTMLElement).scrollIntoView({
								behavior: "smooth",
								block: "center",
							});
							// Wait for scroll animation to complete before showing the next step
							setTimeout(() => {
								setCurrentStepIndex(nextStepIndex);
							}, 500);
						}
					})
					.catch((error) => {
						console.warn(
							`Next tour target not found: ${nextStepSelector}. Reason:`,
							error,
						);
						// If there's an onNextClick handler for the next step, call it before moving forward
						if (nextStep?.onNextClick && elements.reference) {
							nextStep.onNextClick(elements.reference as Element, router);
						}
						setCurrentStepIndex(nextStepIndex);
					})
					.finally(() => {
						setIsLoading(false);
					});
			} else {
				end();
				props?.onFinish?.();
				setIsLoading(false);
			}
		}
	}, [
		currentStepIndex,
		props?.steps,
		elements.reference,
		end,
		props?.onFinish,
		router,
	]);

	const prevStep = useCallback(() => {
		if (currentStepIndex !== null && currentStepIndex > 0) {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	}, [currentStepIndex]);

	useEffect(() => {
		if (
			currentStepIndex === null ||
			props?.steps.length === 0 ||
			!props?.steps[currentStepIndex] ||
			lastExecutedStepRef.current === currentStepIndex // Skip if this step was already executed
		)
			return;

		let isMounted = true;
		const targetSelector = props?.steps[currentStepIndex].target;

		waitForElement(targetSelector)
			.then((target) => {
				if (!isMounted) return;
				refs.setReference(target);
				update();
				target.scrollIntoView({ behavior: "smooth", block: "center" });

				if (props?.steps[currentStepIndex]?.onShow) {
					lastExecutedStepRef.current = currentStepIndex;
					const tour: TourContext = {
						start,
						end,
						nextStep,
						prevStep,
						currentStepIndex,
						currentStep: props?.steps[currentStepIndex],
						steps: props?.steps ?? [],
						isLastStep: currentStepIndex === (props?.steps?.length ?? 0) - 1,
						isEnabled: currentStepIndex !== null,
						disableManualExit: props?.disableManualExit ?? false,
					};
					props.steps[currentStepIndex].onShow(target, tour);
				}
			})
			.catch((error) => {
				console.warn(`Tour target not found: ${targetSelector}`, error);
				end();
			});

		const listener = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !props?.disableManualExit) end();
		};
		document.addEventListener("keydown", listener);

		return () => {
			isMounted = false;
			document.removeEventListener("keydown", listener);
		};
	}, [
		currentStepIndex,
		update,
		end,
		props?.steps,
		props?.disableManualExit,
		refs,
		start,
		nextStep,
		prevStep,
	]);

	return {
		arrowRef,
		context,
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		currentStep: props?.steps?.[currentStepIndex ?? 0]!,
		currentStepIndex: currentStepIndex ?? 0,
		currentTarget: elements.reference,
		end,
		floatingProps: { ref: refs.setFloating, style: floatingStyles },
		isEnabled: currentStepIndex !== null,
		isLastStep: currentStepIndex === (props?.steps?.length ?? 0) - 1,
		nextStep,
		prevStep,
		refs,
		start,
		steps: props?.steps ?? [],
		noPreviousButton: props?.noPreviousButton,
		isLoading,
		disableManualExit: props?.disableManualExit ?? false,
	};
}

export function TourTrigger({
	asChild,
	...props
}: {
	children: React.ReactNode;
	asChild?: boolean;
} & React.ComponentProps<"button">) {
	const tour = useTourContext();
	const Comp = asChild ? Slot : "button";
	return (
		<Comp
			{...props}
			onClick={(event) => {
				if (!event.defaultPrevented) {
					tour?.start();
				}
			}}
		/>
	);
}

const TourContext = createContext<ReturnType<typeof useTour> | null>(null);

export function Tour({
	children,
	steps,
	onFinish,
	isOpen,
	onClose,
	noPreviousButton,
	disableManualExit,
}: {
	children: React.ReactNode;
} & UseTourProps & {
		noPreviousButton?: boolean;
	}) {
	const tour = useTour(
		useMemo(
			() => ({
				steps,
				onFinish,
				isOpen,
				onClose,
				noPreviousButton,
				disableManualExit,
			}),
			[steps, onFinish, isOpen, onClose, noPreviousButton, disableManualExit],
		),
	);
	return <TourContext.Provider value={tour}>{children}</TourContext.Provider>;
}

export function useTourContext() {
	const tour = useContext(TourContext);
	if (!tour)
		throw new Error("useTourContext must be used within a Tour component");
	return tour;
}

export function TourOverlay() {
	const tour = useTourContext();
	if (!tour.isEnabled || !tour.currentTarget) return null;
	const rect = tour.currentTarget?.getBoundingClientRect();
	return (
		<FloatingOverlay
			className="z-[9997]"
			onClick={tour.disableManualExit ? undefined : tour.end}
			lockScroll
		>
			<div
				className="absolute bg-transparent rounded"
				style={{
					top: (rect?.top ?? 0) - 4,
					left: (rect?.left ?? 0) - 4,
					width: (rect?.width ?? 0) + 8,
					height: (rect?.height ?? 0) + 8,
					boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.7)",
				}}
			/>
		</FloatingOverlay>
	);
}

export function TourArrow({ className }: { className?: string }) {
	const tour = useTourContext();
	return (
		<FloatingArrow
			ref={tour.arrowRef}
			context={tour.context}
			className={cn(
				"fill-surface [&>path:first-of-type]:stroke-border [&>path:last-of-type]:stroke-border",
				className,
			)}
		/>
	);
}

export function TourContent({
	children,
	className,
}: { children: React.ReactNode; className?: string }) {
	const tour = useTourContext();
	if (!tour.isEnabled || !tour.currentTarget) return null;
	return (
		<Card
			className={cn("max-w-sm z-[9998]", className)}
			{...tour.floatingProps}
		>
			{children}
		</Card>
	);
}

export function TourStep({ className }: { className?: string }) {
	const tour = useTourContext();
	return (
		<CardContent className={cn("p-4", className)}>
			{tour.currentStep.step}
		</CardContent>
	);
}

export function TourFooter() {
	const tour = useTourContext();
	return (
		<CardFooter className="flex items-center justify-between p-3 gap-4">
			<div className="flex-1">
				{!tour.noPreviousButton && (
					<Button
						size="sm"
						onClick={tour.prevStep}
						disabled={tour.currentStepIndex === 0}
					>
						<Icon kind="arrow-left" />
						Previous
					</Button>
				)}
			</div>
			<div className="flex flex-1 justify-center gap-1">
				{tour.steps.map(({ target }, index) => (
					<div
						key={target}
						className={`w-2 h-2 rounded-full ${index === tour.currentStepIndex ? "bg-jace" : "bg-sidebar"}`}
					/>
				))}
			</div>
			<div className="flex-1 flex justify-end">
				{!tour.currentStep.hideNextButton && (
					<Button
						size="sm"
						intent="brand"
						onClick={tour.nextStep}
						isLoading={tour.isLoading}
					>
						{tour.isLastStep ? "Finish" : "Next"}
						{tour.isLastStep ? null : <Icon kind="arrow-right" />}
					</Button>
				)}
			</div>
		</CardFooter>
	);
}
